import {
  Menu,
  PanelRightClose,
  PanelRightOpen,
} from "lucide-react";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import ContextSwitcher from "./ContextSwitcher";
import ChatFilePreviewPanel from "@/components/operate/ChatFilePreviewPanel";
import HistoryDetailPanel from "@/components/operate/HistoryDetailPanel";
import RunContextPanel from "@/components/operate/RunContextPanel";
import { Button } from "@/components/ui/Button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/Sheet";
import { useOperateStore } from "@/store/operate";
import { useShellStore } from "@/store/shell";
import { usePermissions } from "@/hooks/usePermissions";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { cn } from "@/utils/cn";
import {
  routePosture,
  shellModeFromPosture,
  type ShellMode,
} from "@/utils/posture";
import {
  resolvePlateformeHomeRoute,
  resolveStudioHomeRoute,
} from "@/utils/studioHomeRoute";

interface AppShellProps {
  children: ReactNode;
}

export default function AppShell({ children }: AppShellProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true,
  );
  const [isNarrowScreen, setIsNarrowScreen] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 1024 : false,
  );
  const executionSteps = useOperateStore((state) => state.executionSteps);
  const executionPanelOpen = useOperateStore(
    (state) => state.executionPanelOpen,
  );
  const setExecutionPanelOpen = useOperateStore(
    (state) => state.setExecutionPanelOpen,
  );
  const currentSessionId = useOperateStore((state) => state.currentSessionId);
  const selectedRecipeId = useOperateStore((state) => state.selectedRecipeId);
  const selectedRecipePreview = useOperateStore(
    (state) => state.selectedRecipePreview,
  );
  const selectedExecution = useOperateStore((state) => state.selectedExecution);
  const historyPanelOpen = useOperateStore((state) => state.historyPanelOpen);
  const setHistoryPanelOpen = useOperateStore(
    (state) => state.setHistoryPanelOpen,
  );
  const chatExecutionPanelTarget = useOperateStore(
    (state) => state.chatExecutionPanelTarget,
  );
  const clearChatExecutionPanelTarget = useOperateStore(
    (state) => state.clearChatExecutionPanelTarget,
  );
  const chatFilePanelTarget = useOperateStore(
    (state) => state.chatFilePanelTarget,
  );
  const clearChatFilePanelTarget = useOperateStore(
    (state) => state.clearChatFilePanelTarget,
  );
  const leftPanelWidth = useShellStore((state) => state.leftPanelWidth);
  const setLeftPanelWidth = useShellStore((state) => state.setLeftPanelWidth);
  const rightPanelVisible = useShellStore((state) => state.rightPanelVisible);
  const rightPanelWidth = useShellStore((state) => state.rightPanelWidth);
  const setRightPanelVisible = useShellStore(
    (state) => state.setRightPanelVisible,
  );
  const setRightPanelWidth = useShellStore((state) => state.setRightPanelWidth);
  const toggleRightPanel = useShellStore((state) => state.toggleRightPanel);
  const rightPanelManuallyToggled = useShellStore(
    (state) => state.rightPanelManuallyToggled,
  );
  const setRightPanelManuallyToggled = useShellStore(
    (state) => state.setRightPanelManuallyToggled,
  );
  const autoShowSuppressed = useShellStore((state) => state.autoShowSuppressed);
  const suppressPanel = useShellStore((state) => state.suppressPanel);
  const clearAutoShowSuppressed = useShellStore(
    (state) => state.clearAutoShowSuppressed,
  );
  const { canWrite, canDeploy, canManageUsers } = usePermissions();
  const canBuildRecipes = canWrite("recipes");
  const canBuildDeploy = canDeploy();
  const canBuildExternalControl = canManageUsers();
  const canAccessBuild = canBuildDeploy || canBuildExternalControl;
  const canAccessStudio = canBuildExternalControl || canBuildRecipes;
  const defaultBuildRoute = useMemo(
    () =>
      resolvePlateformeHomeRoute({
        canBuildDeploy,
        canBuildExternalControl,
        canBuildRecipes,
      }),
    [canBuildDeploy, canBuildExternalControl, canBuildRecipes],
  );
  const defaultBuilderRoute = useMemo(
    () =>
      resolveStudioHomeRoute({
        canBuildRecipes,
        canBuildExternalControl,
      }),
    [canBuildRecipes, canBuildExternalControl],
  );
  const isOperateChatRoute = location.pathname.startsWith("/run/chat");
  const isOperateCatalogRoute =
    location.pathname.startsWith("/run/catalog");
  const isOperateHistoryRoute =
    location.pathname.startsWith("/run/history");
  const posture = routePosture(location.pathname);
  const shellMode = shellModeFromPosture(posture);
  const showChatFilePanel = isOperateChatRoute && Boolean(chatFilePanelTarget);
  const showCatalogRecipePanel =
    isOperateCatalogRoute && Boolean(selectedRecipeId);
  const hasRunContext =
    Boolean(chatExecutionPanelTarget) ||
    Boolean(selectedRecipeId) ||
    executionSteps.length > 0 ||
    executionPanelOpen;
  const showExecutionPanel =
    isOperateChatRoute && hasRunContext && !showChatFilePanel;
  const showHistoryPanel =
    isOperateHistoryRoute && Boolean(selectedExecution) && historyPanelOpen;
  const hasRightPanelContent =
    showChatFilePanel ||
    showCatalogRecipePanel ||
    showExecutionPanel ||
    showHistoryPanel;
  const displayRightPanel = rightPanelVisible && hasRightPanelContent;
  const contextToggleRef = useRef<HTMLButtonElement | null>(null);
  const contextTriggerRef = useRef<HTMLElement | null>(null);
  const previousDisplayRightPanelRef = useRef(false);
  const effectiveSidebarOpen = isNarrowScreen
    ? sidebarOpen
    : sidebarOpen;
  const previousRightPanelContentRef = useRef(hasRightPanelContent);
  const previousPostureRef = useRef(posture);
  const desktopSidebarPreferenceRef = useRef(sidebarOpen);
  const isResizingLeftPanelRef = useRef(false);
  const isResizingRightPanelRef = useRef(false);

  const executionSummary = useMemo(() => {
    const totalDuration = executionSteps.reduce((acc, step) => {
      if (step.completedAt && step.startedAt) {
        return acc + (step.completedAt.getTime() - step.startedAt.getTime());
      }
      return acc;
    }, 0);

    const ratioProgress =
      executionSteps.length === 0
        ? 0
        : (executionSteps.filter((step) => step.status === "completed").length /
            executionSteps.length) *
          100;
    const explicitProgress = executionSteps.reduce((acc, step) => {
      if (typeof step.progress === "number" && Number.isFinite(step.progress)) {
        return Math.max(acc, step.progress);
      }
      return acc;
    }, 0);
    const currentProgress = Math.max(ratioProgress, explicitProgress);

    return { currentProgress, totalDuration };
  }, [executionSteps]);


  useEffect(() => {
    if (displayRightPanel && !previousDisplayRightPanelRef.current) {
      const active = document.activeElement;
      contextTriggerRef.current =
        active instanceof HTMLElement && active !== document.body
          ? active
          : contextToggleRef.current;
    }
    previousDisplayRightPanelRef.current = displayRightPanel;
  }, [displayRightPanel]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 1023px)");
    const update = () => {
      setIsNarrowScreen(media.matches);
    };

    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!isNarrowScreen) {
      desktopSidebarPreferenceRef.current = sidebarOpen;
    }
  }, [isNarrowScreen, sidebarOpen]);

  useEffect(() => {
    if (isNarrowScreen) {
      setSidebarOpen(false);
      return;
    }

    setSidebarOpen(desktopSidebarPreferenceRef.current);
  }, [isNarrowScreen]);

  useEffect(() => {
    if (!hasRightPanelContent) {
      // Reset manual toggle flag when all content disappears
      setRightPanelManuallyToggled(false);
    }
  }, [hasRightPanelContent, setRightPanelManuallyToggled]);

  // Close the right panel when the posture changes (Build ↔ Operate) so that
  // panels like Calisto / MetaRecette don't bleed into the other mode.
  // Auto-show suppression persists until the user navigates within the same
  // posture (e.g. Chat → Catalog) or manually toggles the panel.
  useEffect(() => {
    if (previousPostureRef.current !== posture) {
      if (posture === "builder") {
        clearAutoShowSuppressed();
        setRightPanelVisible(true);
      } else {
        suppressPanel();
      }
      previousPostureRef.current = posture;
    }
  }, [clearAutoShowSuppressed, posture, setRightPanelVisible, suppressPanel]);

  // Clear auto-show suppression when the user navigates within the same posture.
  // We track pathname changes — if posture stays the same, it means in-mode navigation.
  const previousPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (previousPathnameRef.current !== location.pathname) {
      const prevPosture = routePosture(previousPathnameRef.current);
      const currPosture = routePosture(location.pathname);
      if (prevPosture === currPosture && autoShowSuppressed) {
        clearAutoShowSuppressed();
      }
      previousPathnameRef.current = location.pathname;
    }
  }, [location.pathname, autoShowSuppressed, clearAutoShowSuppressed]);

  useEffect(() => {
    // Suppress auto-show during posture transitions — the boolean stays true
    // until the user navigates within the same posture or manually toggles.
    if (autoShowSuppressed) {
      previousRightPanelContentRef.current = hasRightPanelContent;
      return;
    }
    // Auto-show panel when content appears
    if (!previousRightPanelContentRef.current && hasRightPanelContent) {
      setRightPanelVisible(true);
    }
    // Auto-hide panel when content disappears (unless user manually toggled it)
    if (
      previousRightPanelContentRef.current &&
      !hasRightPanelContent &&
      !rightPanelManuallyToggled
    ) {
      setRightPanelVisible(false);
    }
    previousRightPanelContentRef.current = hasRightPanelContent;
  }, [
    autoShowSuppressed,
    hasRightPanelContent,
    rightPanelManuallyToggled,
    setRightPanelVisible,
  ]);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      if (isResizingLeftPanelRef.current) {
        setLeftPanelWidth(event.clientX);
        return;
      }
      if (!isResizingRightPanelRef.current) return;
      const nextWidth = window.innerWidth - event.clientX;
      setRightPanelWidth(nextWidth);
    };

    const stopResize = () => {
      const wasResizingLeft = isResizingLeftPanelRef.current;
      const wasResizingRight = isResizingRightPanelRef.current;
      if (!wasResizingLeft && !wasResizingRight) return;
      isResizingLeftPanelRef.current = false;
      isResizingRightPanelRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stopResize);
    window.addEventListener("pointercancel", stopResize);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stopResize);
      window.removeEventListener("pointercancel", stopResize);
    };
  }, [setLeftPanelWidth, setRightPanelWidth]);

  const startLeftPanelResize = () => {
    isResizingLeftPanelRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const startRightPanelResize = () => {
    isResizingRightPanelRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  useKeyboardShortcuts({
    shortcuts: [
      {
        key: "\\",
        ctrl: true,
        shift: true,
        callback: () => {
          if (hasRightPanelContent) {
            toggleRightPanel();
          }
        },
      },
    ],
  });

  const handleModeChange = (nextMode: ShellMode) => {
    if (nextMode === shellMode) {
      return;
    }

    // Suppress the panel for a short window to prevent auto-show from
    // immediately re-opening it during post-navigation renders.
    suppressPanel();

    if (nextMode === "studio") {
      navigate(canAccessStudio ? defaultBuilderRoute : defaultBuildRoute);
      return;
    }

    navigate("/run/chat");
  };

  const renderRightPanelContent = () => {
    if (showChatFilePanel && chatFilePanelTarget) {
      return (
        <ChatFilePreviewPanel
          file={chatFilePanelTarget}
          onClose={clearChatFilePanelTarget}
        />
      );
    }

    if (showCatalogRecipePanel) {
      return (
        <RunContextPanel
          key={`catalog-context:${selectedRecipeId ?? "none"}`}
          sessionId={null}
          recipeId={selectedRecipeId}
          recipePreview={selectedRecipePreview}
          executionSteps={[]}
          executionPanelOpen={false}
          onToggleExecution={() => undefined}
          totalDuration={0}
          currentProgress={0}
          showExecutionPanel={false}
        />
      );
    }

    if (showExecutionPanel) {
      return (
        <RunContextPanel
          key={`run-context:${selectedRecipeId ?? "none"}:${
            chatExecutionPanelTarget?.executionId ?? "none"
          }`}
          sessionId={currentSessionId!}
          recipeId={selectedRecipeId}
          recipePreview={selectedRecipePreview}
          executionSteps={executionSteps}
          executionPanelOpen={executionPanelOpen}
          onToggleExecution={() => setExecutionPanelOpen(!executionPanelOpen)}
          executionDetailTarget={chatExecutionPanelTarget}
          onCloseExecutionDetail={clearChatExecutionPanelTarget}
          totalDuration={executionSummary.totalDuration}
          currentProgress={executionSummary.currentProgress}
        />
      );
    }

    if (selectedExecution) {
      return (
        <div className="flex h-full flex-col overflow-hidden">
          <div className="border-b border-border px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold">
                  {t("shell.executionDetailTitle")}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {t("shell.executionDetailDescription")}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setHistoryPanelOpen(false)}
              >
                {t("common.hide")}
              </Button>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <HistoryDetailPanel execution={selectedExecution} />
          </div>
        </div>
      );
    }

    return null;
  };

  const modeSwitcher = (
    <div className="rounded-full border border-border bg-card p-1">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => handleModeChange("run")}
          aria-current={shellMode === "run" ? "page" : undefined}
          data-testid="shell-mode-run"
          className={cn(
            "rounded-full px-5 py-2 text-sm font-semibold transition-colors",
            shellMode === "run"
              ? "bg-primary text-primary-foreground"
              : "text-foreground hover:bg-card-raised",
          )}
        >
          {t("common.run")}
        </button>
        <button
          type="button"
          onClick={() => handleModeChange("studio")}
          disabled={!canAccessStudio && !canAccessBuild}
          aria-current={shellMode === "studio" ? "page" : undefined}
          data-testid="shell-mode-studio"
          className={cn(
            "rounded-full px-5 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50",
            shellMode === "studio"
              ? "bg-primary text-primary-foreground"
              : "text-foreground hover:bg-card-raised",
          )}
        >
          {t("common.studio")}
        </button>
      </div>
    </div>
  );
  const deploymentScopeControls = (
    <div
      className={cn(
        "flex min-w-0 items-center gap-3",
        isNarrowScreen ? "flex-1" : "shrink-0",
      )}
    >
      <div className="min-w-0">
        <ContextSwitcher />
      </div>
    </div>
  );
  const topbarActions = (
    <div className="flex min-w-0 items-center justify-end gap-3">
      <div className="flex min-w-0 items-center gap-2">
        {hasRightPanelContent ? (
          <Button
            variant={displayRightPanel ? "secondary" : "outline"}
            size="sm"
            className="gap-2 px-3"
            ref={contextToggleRef}
            data-testid="shell-context-toggle"
            onClick={() => {
              toggleRightPanel();
            }}
            aria-label={t("shell.toggleContextPanel")}
            title={t("shell.toggleRightPanel")}
          >
            {displayRightPanel ? (
              <PanelRightClose className="h-4 w-4" />
            ) : (
              <PanelRightOpen className="h-4 w-4" />
            )}
            {!isNarrowScreen && (
              <span className="hidden 2xl:inline">{t("common.context")}</span>
            )}
          </Button>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-background">
      {isNarrowScreen && effectiveSidebarOpen && (
        <button
          type="button"
          aria-label={t("common.close")}
          className="fixed inset-0 z-30 bg-foreground/40 dark:bg-background/80 lg:hidden"
          onClick={() => {
            setSidebarOpen(false);
            setRightPanelVisible(false);
          }}
        />
      )}

      <Sidebar
        open={effectiveSidebarOpen}
        width={leftPanelWidth}
        mobile={isNarrowScreen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      {effectiveSidebarOpen && !isNarrowScreen && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={t("shell.resizeNavigationPanel")}
          className="group relative hidden w-1 cursor-col-resize bg-transparent lg:block"
          onPointerDown={startLeftPanelResize}
        >
          <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border transition-colors group-hover:bg-primary/50" />
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="relative z-20 flex h-16 items-center gap-3 border-b border-border bg-background px-3 sm:gap-4 sm:px-4">
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="pointer-events-auto">{modeSwitcher}</div>
          </div>
          <div className="relative z-10 flex min-w-0 max-w-[42%] items-center gap-2">
            {!effectiveSidebarOpen ? (
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 shrink-0 rounded-full"
                data-testid="shell-nav-toggle"
                onClick={() => setSidebarOpen(true)}
                aria-expanded={false}
                aria-label={t("shell.openNavigation")}
              >
                <Menu className="h-5 w-5" />
              </Button>
            ) : null}
            {deploymentScopeControls}
          </div>
          <div className="relative z-10 ml-auto flex min-w-0 max-w-[42%] shrink-0 items-center justify-end">
            {topbarActions}
          </div>
        </div>

        <div className="flex min-h-0 flex-1 overflow-hidden">
          <div className="min-w-0 flex-1 overflow-auto">{children}</div>

          {displayRightPanel && (
            <>
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label={t("shell.resizeContextPanel")}
                className="group relative hidden w-1 cursor-col-resize bg-transparent lg:block"
                onPointerDown={startRightPanelResize}
              >
                <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border transition-colors group-hover:bg-primary/50" />
              </div>
            </>
          )}

          {displayRightPanel && !isNarrowScreen && (
            <>
              <aside
                className="border-l border-border bg-card"
                style={{ width: `${rightPanelWidth}px` }}
              >
                {renderRightPanelContent()}
              </aside>
            </>
          )}
        </div>
      </div>

      {isNarrowScreen && hasRightPanelContent && (
        <Sheet
          open={displayRightPanel}
          onOpenChange={setRightPanelVisible}
        >
          <SheetContent
            closeLabel={t("common.close")}
            data-testid="shell-context-sheet"
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              const target =
                contextTriggerRef.current ?? contextToggleRef.current;
              target?.focus();
            }}
          >
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div>
              <SheetTitle className="text-sm font-semibold">
                {t("common.context")}
              </SheetTitle>
              <SheetDescription className="text-xs text-muted-foreground">
                {t("shell.emptyContextDescription")}
              </SheetDescription>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {renderRightPanelContent()}
          </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
